import type { PublicGuest, PublicState } from '@karozik/shared';
import { Avatar, cn, FACE_MODE_LABELS, FaceMashup } from '@karozik/ui';
import { Podium, RoundWinners } from './BlindtestScreen';

const COLORS = ['bg-pink', 'bg-violet', 'bg-cyan text-night', 'bg-sun text-night'];
const LETTERS = ['A', 'B', 'C', 'D'];

function Pair({ pair }: { pair: [PublicGuest, PublicGuest] }) {
  return (
    <span className="flex min-w-0 flex-1 flex-col gap-[0.8vh]">
      {pair.map((g) => (
        <span key={g.id} className="flex min-w-0 items-center gap-[0.8vw]">
          <Avatar guest={g} className="!h-[5vh] !w-[5vh] !text-[3vh]" />
          <span className="truncate">{g.pseudo}</span>
        </span>
      ))}
    </span>
  );
}

/** Têtes Mêlées on the projector: the mixed picture, the four pairs, the reveal, the podium. */
export function FacesScreen({ state, now, clockSkew }: { state: PublicState; now: number; clockSkew: number }) {
  const f = state.faces!;
  const playS = (f.playMs + f.sharpMs) / 1000;
  const left = Math.min(f.stage === 'playing' ? playS : 99, Math.max(0, Math.ceil((f.stageEndsAt - now) / 1000)));
  const lastChance = f.stage === 'playing' && f.startedAt != null && now >= f.startedAt + f.playMs;
  const token = state.event.joinUrl.split('/j/')[1];
  const right = f.correctIndex != null && f.choices ? f.choices[f.correctIndex] : null;

  return (
    <div className="absolute inset-0 z-40 flex flex-col bg-night px-[4vw] py-[4vh]">
      <header className="flex items-center justify-between">
        <div className="font-display text-[5vh] font-bold text-gradient">🤪 Têtes Mêlées</div>
        {f.round > 0 && f.stage !== 'final' && (
          <div className="text-[3vh] text-ink-dim">
            Manche <b className="text-ink">{f.round}</b> / {f.totalRounds}
          </div>
        )}
      </header>

      {f.stage === 'intro' && (
        <div className="flex flex-1 items-center justify-center gap-[5vw]">
          <div>
            <div className="font-display text-[9vh] leading-tight font-bold">
              Qui se cache
              <br />
              là-dedans ?
            </div>
            <p className="mt-[2vh] max-w-[42vw] text-[3vh] text-ink-dim">
              Deux têtes de la soirée, mélangées. L’image s’affine peu à peu : trouvez la bonne paire sur vos téléphones, le plus vite possible.
            </p>
            <p className="mt-[2vh] max-w-[42vw] text-[2.6vh] text-ink-faint">
              📸 {state.faceCount} têtes dans le jeu. Pas encore la tienne ? Ouvre KaroZik et touche « Prendre mon selfie ».
            </p>
            <div className="font-display mt-[4vh] text-[14vh] font-bold">{left}</div>
          </div>
          <img src={`/api/join/${token}/qr.svg`} alt="" className="h-[40vh] w-[40vh] rounded-[3vh] bg-white p-[1.5vh]" />
        </div>
      )}

      {f.stage === 'playing' && f.choices && (
        <div className="mt-[2vh] flex min-h-0 flex-1 gap-[3vw]">
          <div className="flex shrink-0 flex-col items-center">
            <div className="rounded-[1vh] bg-[#f3ebdf] p-[1vh] pb-[3.5vh] shadow-[0_2vh_6vh_rgba(0,0,0,0.6)]">
              <FaceMashup faces={f} durationMs={f.playMs} clockSkew={clockSkew} sharp={false} className="h-[min(66vh,44vw)]" />
            </div>
            <div className={cn('mt-[1.5vh] text-[2.4vh] font-bold tracking-[0.25em] uppercase', lastChance ? 'animate-pulse-soft text-sun' : 'text-ink-faint')}>
              {lastChance ? 'Dernière chance !' : f.mode && FACE_MODE_LABELS[f.mode]}
            </div>
          </div>
          <div className="flex min-w-0 flex-1 flex-col">
            <div className="flex items-center gap-[2vw]">
              <div className="h-[2vh] flex-1 overflow-hidden rounded-full bg-white/10">
                <div className="bg-gradient-neon h-full transition-all duration-500" style={{ width: `${(left / playS) * 100}%` }} />
              </div>
              <div className="font-display w-[8vw] text-right text-[7vh] font-bold">{left}</div>
            </div>
            <div className="mt-[3vh] grid flex-1 grid-cols-2 gap-[1.5vw]">
              {f.choices.map((pair, i) => (
                <div key={i} className={cn('flex min-w-0 items-center gap-[1.2vw] rounded-[3vh] px-[2vw] text-[min(3.6vh,2.2vw)] leading-tight font-bold', COLORS[i])}>
                  <span className="font-display text-[7vh] opacity-60">{LETTERS[i]}</span>
                  <Pair pair={pair} />
                </div>
              ))}
            </div>
            <div className="mt-[2vh] text-center text-[3vh] text-ink-dim">
              {f.answered.length} réponse{f.answered.length > 1 ? 's' : ''}
            </div>
          </div>
        </div>
      )}

      {f.stage === 'reveal' && f.images && (
        <div className="flex min-h-0 flex-1 items-center gap-[4vw]">
          <FaceMashup faces={f} durationMs={f.playMs} clockSkew={clockSkew} sharp className="h-[min(62vh,48vw)] shrink-0 rounded-[2vh]" />
          <div className="min-w-0 flex-1">
            <div className="text-[3.4vh] font-semibold text-lime">C’était…</div>
            <div className="mt-[2vh] flex gap-[1.5vw]">
              {[f.images.a, f.images.b].map((src, i) => (
                <figure key={src} className="flex min-w-0 flex-col items-center gap-[1vh]">
                  <img src={src} alt="" className={cn('h-[min(28vh,15vw)] rounded-[2vh] border-[0.6vh] object-cover', i === 0 ? 'border-sun' : 'border-cyan')} />
                  {right && <figcaption className="font-display max-w-full truncate text-[min(4vh,2.4vw)] font-bold">{right[i]!.pseudo}</figcaption>}
                </figure>
              ))}
            </div>
            <RoundWinners leaderboard={f.leaderboard} lastPoints={f.lastPoints} />
          </div>
        </div>
      )}

      {f.stage === 'final' && <Podium board={f.leaderboard} />}
    </div>
  );
}
